import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchFinanceDetail, createFinanceDraft } from "services/customers.service";
import { FinanceYearDetail } from "types/Customer";

// Service is .js with default param = null so TS infers 4th param as null | undefined; assert correct signature
type FetchFinanceDetailFn = (
  companyId: string,
  customerId: string,
  year: number,
  financeId?: string | null
) => ReturnType<typeof fetchFinanceDetail>;
const fetchFinanceDetailTyped = fetchFinanceDetail as FetchFinanceDetailFn;

interface FinanceDetailState {
  financeDetail: FinanceYearDetail | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
}

const initialState: FinanceDetailState = {
  financeDetail: null,
  isLoading: false,
  isRefreshing: false,
  error: null,
};

// Empty finance detail skeleton used as 404 fallback by fetch thunks
export const createEmptyFinanceDetail = (year: number): FinanceYearDetail => ({
  year,
  exists: false,
  financeRecord: null,
  masterAgreementFileId: null,
  sanctionsDocuments: {
    eu: [],
    usa: [],
    lt: [],
  },
  forms: [],
  vatChecks: [],
  sanctionsSettings: {
    isEuSanctionsEnabled: false,
    isUsaSanctionsEnabled: false,
    isLtSanctionsEnabled: false,
  },
  vatCheckSettings: {
    isVatCheckEnabled: false,
  },
  hasCompletedQuestionnaireInPast: false,
  skipQuestionnaireRequirementIfCompletedInPast: true,
});

export const fetchFinanceDetailAsync = createAsyncThunk(
  "financeDetail/fetch",
  async (
    {
      companyId,
      customerId,
      year,
      financeId,
    }: {
      companyId: string;
      customerId: string;
      year: number;
      financeId?: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      return await fetchFinanceDetail(companyId, customerId, year, financeId);
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return createEmptyFinanceDetail(year);
      }
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch finance detail");
    }
  }
);

// New thunk for background refresh (doesn't show full-page spinner)
export const fetchFinanceDetailRefreshAsync = createAsyncThunk(
  "financeDetail/refresh",
  async (
    {
      companyId,
      customerId,
      year,
      financeId,
    }: {
      companyId: string;
      customerId: string;
      year: number;
      financeId?: string | null;
    },
    { rejectWithValue }
  ) => {
    try {
      return await fetchFinanceDetailTyped(companyId, customerId, year, financeId ?? undefined);
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return createEmptyFinanceDetail(year);
      }
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to refresh finance detail");
    }
  }
);

// Create a draft finance (status 'draft') when user clicks "Create new", then fetch full detail by its id.
export const fetchFinanceDetailCreateDraftAsync = createAsyncThunk(
  "financeDetail/createDraft",
  async (
    {
      companyId,
      customerId,
      year,
    }: {
      companyId: string;
      customerId: string;
      year: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const { financeRecord } = await createFinanceDraft(companyId, customerId, year);
      if (!financeRecord?.id) {
        return rejectWithValue("Draft response missing financeRecord.id");
      }
      return await fetchFinanceDetail(companyId, customerId, year, financeRecord.id);
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return createEmptyFinanceDetail(year);
      }
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to create draft");
    }
  }
);

const financeDetailSlice = createSlice({
  name: "financeDetail",
  initialState,
  reducers: {
    setDestroy: (state) => {
      state.financeDetail = null;
      state.isLoading = false;
      state.isRefreshing = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Initial fetch (shows full-page spinner)
      .addCase(fetchFinanceDetailAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFinanceDetailAsync.fulfilled, (state, action) => {
        state.financeDetail = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchFinanceDetailAsync.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || "Failed to fetch finance detail";
        state.isLoading = false;
      })
      // Background refresh (keeps UI visible)
      .addCase(fetchFinanceDetailRefreshAsync.pending, (state) => {
        state.isRefreshing = true;
        state.error = null;
      })
      .addCase(fetchFinanceDetailRefreshAsync.fulfilled, (state, action) => {
        state.financeDetail = action.payload;
        state.isRefreshing = false;
      })
      .addCase(fetchFinanceDetailRefreshAsync.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || "Failed to refresh finance detail";
        state.isRefreshing = false;
      })
      // Create draft (POST draft then fetch by id)
      .addCase(fetchFinanceDetailCreateDraftAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFinanceDetailCreateDraftAsync.fulfilled, (state, action) => {
        state.financeDetail = action.payload as FinanceYearDetail;
        state.isLoading = false;
      })
      .addCase(fetchFinanceDetailCreateDraftAsync.rejected, (state, action) => {
        state.error = (action.payload as string) || action.error.message || "Failed to create draft";
        state.isLoading = false;
      });
  },
});

export const { setDestroy } = financeDetailSlice.actions;

export default financeDetailSlice.reducer;
